import Handsontable from 'handsontable';
import { TextEditor } from 'handsontable/editors';
import { registerCellType } from 'handsontable/cellTypes';
import { numericValidator } from '@/validators';
import { formatNumber } from '@/helpers';
import { _isNotEmpty, _notNil } from '@/littledash';

export const exponentialRenderer = (
  hotInstance: Handsontable.Core,
  td: HTMLTableCellElement,
  row: number,
  column: number,
  prop: string | number,
  value: string | number,
  cellProperties: Handsontable.CellProperties
): HTMLTableCellElement => {
  Handsontable.renderers.NumericRenderer(hotInstance, td, row, column, prop, value, cellProperties);

  td.innerHTML = '';

  if (_isNotEmpty(value)) {
    const valueAsNumber = formatNumber(Number(value));
    td.innerText = isNaN(valueAsNumber as number) ? String(value) : String(valueAsNumber);
  }

  return td;
};

export class ExponentialEditor extends TextEditor {
  static type = 'numeric-expo';

  private _initialValue?: string | number;
  private _isOpened = false;

  static register(type = ExponentialEditor.type): void {
    registerCellType(type, {
      editor: ExponentialEditor,
      renderer: exponentialRenderer,
      validator: (value: string | number, callback: (valid: boolean) => void) => {
        const stringValue = typeof value === 'string' ? value : (value?.toString() ?? '');
        callback(numericValidator(stringValue, false));
      },
    });
  }

  /**
   * Factory for creating numeric validators with optional required flag
   */
  static numericValidatorFactory(required?: boolean) {
    return (value: string | number, callback: (valid: boolean) => void): void => {
      const stringValue = typeof value === 'string' ? value : (value?.toString() ?? '');
      callback(numericValidator(stringValue, required));
    };
  }

  init(): void {
    super.init();
  }

  createElements(): void {
    super.createElements();

    if (_notNil(this.TEXTAREA)) {
      this.TEXTAREA.classList.add('exponential-editor');

      // Handle keyboard events
      this.TEXTAREA.addEventListener('keydown', (e) => {
        // Handle Enter key to finish editing and move to next row
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          const nextRow = this.row + 1;
          const col = this.col;
          this.finishEditing(false);
          // Move to next row (same behavior as text fields)
          if (nextRow < this.hot.countRows()) {
            this.hot.selectCell(nextRow, col);
          }
          return;
        }

        // Handle Escape key to cancel editing
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          this.finishEditing(true); // restore original value
          return;
        }

        // Stop propagation for other keys to prevent Handsontable interference
        e.stopPropagation();
      });
    }
  }

  /**
   * Prepare editor - override WITHOUT calling super for compatibility
   */
  prepare(
    row: number,
    col: number,
    prop: string | number,
    td: HTMLTableCellElement,
    originalValue: unknown,
    cellProperties: Handsontable.CellProperties
  ): void {
    // Manually set all properties (don't call super.prepare)
    this.row = row;
    this.col = col;
    this.prop = prop;
    this.TD = td;
    this.originalValue = originalValue;
    this.cellProperties = cellProperties;
    this._initialValue = originalValue as string | number | undefined;
  }

  getValue(): string {
    if (this.TEXTAREA instanceof HTMLTextAreaElement) {
      return this.TEXTAREA.value;
    }
    return '';
  }

  setValue(newValue: string | number): void {
    if (this.TEXTAREA instanceof HTMLTextAreaElement) {
      this.TEXTAREA.value = String(newValue ?? '');
    }
  }

  /**
   * Open editor - manually handle
   */
  open(_event?: Event): void {
    if (!_notNil(this.TEXTAREA)) {
      this.createElements();
    }

    if (_notNil(this._initialValue)) {
      this.setValue(this._initialValue);
    }

    if (this.TEXTAREA instanceof HTMLTextAreaElement && _notNil(this.TD)) {
      if (!this.TD.contains(this.TEXTAREA)) {
        this.TD.appendChild(this.TEXTAREA);
        this.TD.classList.add('htNumeric');
      }

      this.TEXTAREA.style.display = 'block';
      this.TEXTAREA.style.width = `${this.TD.offsetWidth}px`;
      this.TEXTAREA.style.height = `${this.TD.offsetHeight}px`;
      this.TEXTAREA.style.backgroundColor = '#fff';
      this.TEXTAREA.style.position = 'absolute';
      this.TEXTAREA.style.top = '0';
      this.TEXTAREA.style.left = '0';
      this.TEXTAREA_PARENT.style.position = 'relative';

      this.TEXTAREA.focus();
      // Position cursor at end of text (like text fields) instead of selecting all
      this.TEXTAREA.selectionStart = this.TEXTAREA.selectionEnd = this.TEXTAREA.value.length;
    }

    this._isOpened = true;
  }

  /**
   * Close editor - manually handle + call render
   */
  close(): void {
    if (!this._isOpened) {
      return;
    }

    if (this.TEXTAREA instanceof HTMLTextAreaElement) {
      this.TEXTAREA.style.display = 'none';

      if (_notNil(this.TD) && this.TD.contains(this.TEXTAREA)) {
        this.TD.removeChild(this.TEXTAREA);
      }
    }

    this._isOpened = false;
    this.hot.render();
  }

  /**
   * Refresh dimensions - override without calling super
   */
  refreshDimensions(): void {
    if (this.TEXTAREA instanceof HTMLTextAreaElement && _notNil(this.TD)) {
      this.TEXTAREA.style.width = `${this.TD.offsetWidth}px`;
      this.TEXTAREA.style.height = `${this.TD.offsetHeight}px`;
    }
  }

  focus(): void {
    if (this.TEXTAREA instanceof HTMLTextAreaElement) {
      this.TEXTAREA.focus();
      // Position cursor at end of text (like text fields) instead of selecting all
      this.TEXTAREA.selectionStart = this.TEXTAREA.selectionEnd = this.TEXTAREA.value.length;
    }
  }

  /**
   * Finish editing - check _isOpened first
   */
  finishEditing(restoreOriginalValue = false, ctrlDown = false): void {
    // Only process if editor was actually opened
    if (!this._isOpened) {
      super.finishEditing(restoreOriginalValue, ctrlDown);
      return;
    }

    if (restoreOriginalValue) {
      if (_notNil(this._initialValue)) {
        this.hot.setDataAtCell(this.row, this.col, this._initialValue);
      }
    } else {
      const currentValue = this.getValue();
      if (currentValue !== this._initialValue) {
        this.hot.setDataAtCell(this.row, this.col, currentValue);
      }
    }

    this._isOpened = false;
    super.finishEditing(restoreOriginalValue, ctrlDown);
  }
}
